import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { App, AUTH_BYPASS } from "./App";

const rootElement = document.getElementById("root");

async function enableMocking() {
  if (import.meta.env.VITE_MSW !== "true") return;
  const { worker } = await import("./tests/mocks/browser");
  await worker.start({
    onUnhandledRequest: "bypass",
    // Ensure the worker is fully active before the app renders.
    // Prevents race condition where early fetches bypass MSW.
    serviceWorker: { options: { scope: "/" } },
    quiet: true,
  });
  // Extra safety: wait for the controlling service worker to be active.
  if (navigator.serviceWorker.controller === null) {
    await new Promise<void>((resolve) => {
      navigator.serviceWorker.addEventListener(
        "controllerchange",
        () => resolve(),
        { once: true },
      );
    });
  }
}

async function start() {
  if (!rootElement) return;

  await enableMocking();

  // Dev-only: apiClient fails closed without an MSAL context; bypass mode never sets one.
  if (
    import.meta.env.DEV &&
    AUTH_BYPASS &&
    import.meta.env.VITE_MSW === "true"
  ) {
    const { setMsalContext } = await import("./services/apiClient");
    setMsalContext(
      {
        acquireTokenSilent: () =>
          Promise.resolve({ accessToken: "local-dev-msw" }),
        acquireTokenRedirect: () => Promise.resolve(),
      } as never,
      {
        localAccountId: "local-dev-msw",
        username: "dev@sj.se",
        idTokenClaims: {},
      } as never,
    );
  }

  let msalInstance;
  if (!AUTH_BYPASS) {
    const { PublicClientApplication } = await import("@azure/msal-browser");
    const { msalConfig } = await import("./auth/authConfig");
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
    await msalInstance.handleRedirectPromise();
  }

  createRoot(rootElement).render(
    <StrictMode>
      <App msalInstance={msalInstance} />
    </StrictMode>,
  );
}

void start();
